import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * RTK Query slice for AI marketing-content generation — REP-205
 * (Listing/Push pillar, Phase 2). TEXT ONLY: an editable email draft +
 * social caption variants (with accessibility alt text). NO image
 * generation — the broker's existing property photos are the only imagery.
 *
 * Endpoints are keyed by the lister's seller-rep Project id
 * (`listingProjectId`) for URL symmetry with the other listing routes; the
 * body MAY carry the resolved `listingSheetId` directly (the PushComposer
 * already has it via useGetListingSheetBySellerRepProject) so the backend
 * can skip re-resolving.
 *
 *   POST /api/listings/:id/content/generate  → generate drafts
 *   GET  /api/listings/:id/content           → list prior drafts
 *
 * Keep enum + body/result shapes in sync with
 * `repsity-service/src/dtos/content.dto.ts`. Note: `baseUrl: "/api/listings"`
 * and the FE strips `/api` upstream (mirrors pushApi / listingReportApi).
 *
 * STORE REGISTRATION (done by the Frontend-wire agent, not here): add the
 * three insertion points in `store.ts` —
 *   import { contentApi } from "./slices/content/contentApi";
 *   [contentApi.reducerPath]: contentApi.reducer,
 *   ...concat(contentApi.middleware)
 */

// ─── Enum mirror (content.dto.ts ContentKind) ──────────────────────────
// Only TEXT kinds exist in Phase 2 — there is NO image generation.
export type ContentKind = "EMAIL_DRAFT" | "SOCIAL_CAPTION";

/** A persisted, editable email draft (subject + email-safe HTML fragment). */
export interface EmailDraft {
  id: string;
  subject: string;
  bodyHtml: string;
}

/** A single social caption variant + its accessibility alt text. */
export interface SocialCaption {
  id: string;
  platform: string;
  caption: string;
  altText?: string | null;
}

/** Body of POST /api/listings/:id/content/generate. */
export interface GenerateContentBody {
  /** Optional resolved sheet so the backend can skip re-resolving. */
  listingSheetId?: string;
  /** Which kinds to produce. Empty/omitted = generate all kinds. */
  kinds?: ContentKind[];
  /** Optional broker tone/angle nudge folded into the prompt. */
  instructions?: string;
}

/** Result returned by generate — editable drafts the FE can pipe into the composer. */
export interface GenerateContentResult {
  listingSheetId: string;
  email?: EmailDraft | null;
  captions?: SocialCaption[];
  /**
   * False when the listing is NOT cleared for public marketing — the FE
   * shows an orange nudge (opinionated-not-blocking), never a hard block.
   */
  publicListingPermitted: boolean;
}

/** Shape returned by GET /api/listings/:id/content (most recent first). */
export interface ListContentResult {
  listingSheetId: string;
  email: Array<EmailDraft & { createdAt: string }>;
  captions: Array<SocialCaption & { createdAt: string }>;
}

export const contentApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "contentApi",
    // :id = the lister's seller-rep Project id (listingProjectId).
    baseUrl: "/api/listings",
  }),
  reducerPath: "contentApi",
  tagTypes: ["Content"],
  endpoints: (builder) => ({
    /**
     * POST /api/listings/:id/content/generate
     *
     * Generate email + social-caption drafts grounded in the ListingSheet +
     * PropertyFeatureEntry data. Always returns EDITABLE drafts — nothing is
     * sent here. Regenerate = call again with the same kinds.
     */
    generateContent: builder.mutation<
      { data: GenerateContentResult },
      { listingProjectId: string; body: GenerateContentBody }
    >({
      query: ({ listingProjectId, body }) => ({
        url: `/${listingProjectId}/content/generate`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { listingProjectId }) => [
        { type: "Content", id: listingProjectId },
      ],
    }),

    /**
     * GET /api/listings/:id/content
     *
     * List previously generated drafts for this listing, split into email
     * drafts + caption variants. Used to hydrate the modal on open so the
     * broker sees their last generation without re-spending a model call.
     */
    getContent: builder.query<{ data: ListContentResult }, string>({
      query: (listingProjectId) => `/${listingProjectId}/content`,
      providesTags: (_result, _error, id) => [{ type: "Content", id }],
    }),
  }),
});

export const { useGenerateContentMutation, useGetContentQuery } = contentApi;
